import { useState } from 'react';
import { useTextGeneration } from '@/hooks/text-generation/useTextGeneration';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

export interface ProjectGenerationInput {
  userGoal: string;
}

export interface ProjectGenerationResult {
  title: string;
  goal: string;
  description: string;
  imagePrompt: string;
  followUpQuestions: string[]; // Exactly 2 questions
}

export interface UseProjectGenerationReturn {
  generateProjectData: (input: ProjectGenerationInput) => Promise<ProjectGenerationResult>;
  isGenerating: boolean;
  error: string | null;
}

/**
 * Hook for AI-powered project generation
 * Transforms user input into structured project data optimized for task recommendations
 */
export function useProjectGeneration(): UseProjectGenerationReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { generateText } = useTextGeneration();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  const generateProjectData = async (input: ProjectGenerationInput): Promise<ProjectGenerationResult> => {
    // 🎯 UPFRONT CREDIT CHECK - Prevent operation if no credits available
    if (usageData.remainingBalance === 0) {
      console.log('[useProjectGeneration] Insufficient credits - blocking project generation and showing modal');
      openOutOfCreditsModal();
      throw new Error('Insufficient credits');
    }

    console.log(`[useProjectGeneration] Credits available (${usageData.remainingBalance}) - proceeding with project generation`);

    setIsGenerating(true);
    setError(null);

    try {
      console.log('[useProjectGeneration] Starting AI project generation:', {
        inputLength: input.userGoal.length,
        input: input.userGoal.substring(0, 100) + '...'
      });

      // Call text generation endpoint with project_generation type
      const generatedText = await generateText({
        message: input.userGoal,
        type: 'project_generation',
        appName: 'Project Creator',
        functionName: 'Project Data Generation'
      });

      console.log('[useProjectGeneration] Raw AI response:', generatedText);

      // Parse JSON response from AI
      let parsedData: ProjectGenerationResult;
      try {
        // Clean up any potential markdown formatting or extra text
        const cleanJson = generatedText
          .replace(/^```json\s*\n?/gm, '')
          .replace(/^```\s*\n?/gm, '')
          .replace(/\n?```$/gm, '')
          .trim();

        parsedData = JSON.parse(cleanJson);

        // Validate required fields
        if (!parsedData.title || !parsedData.goal || !parsedData.description || !parsedData.imagePrompt || !parsedData.followUpQuestions) {
          throw new Error('Missing required fields in AI response');
        }

        // Validate followUpQuestions is an array with exactly 2 items
        if (!Array.isArray(parsedData.followUpQuestions) || parsedData.followUpQuestions.length !== 2) {
          throw new Error('followUpQuestions must be an array with exactly 2 items');
        }

        // Ensure character limits are respected with increased limits
        parsedData.title = parsedData.title.substring(0, 50);
        parsedData.goal = parsedData.goal.substring(0, 60);
        parsedData.description = parsedData.description.substring(0, 300);
        parsedData.imagePrompt = parsedData.imagePrompt.substring(0, 200);
        
        // Ensure follow-up questions are reasonable length
        parsedData.followUpQuestions = parsedData.followUpQuestions.map(q => q.substring(0, 150));

      } catch (parseError) {
        console.error('[useProjectGeneration] Failed to parse AI response:', parseError);
        console.error('[useProjectGeneration] Raw response was:', generatedText);
        
        // Fallback: Create intelligent fallback data preserving user intent
        const userGoal = input.userGoal;
        const fallbackTitle = userGoal.length > 50 ? `${userGoal.substring(0, 47)}...` : userGoal;
        
        parsedData = {
          title: fallbackTitle, // Preserve user's project name with smart truncation
          goal: userGoal.length > 60 ? `${userGoal.substring(0, 57)}...` : userGoal,
          description: `Strategic plan to achieve: ${userGoal}`,
          imagePrompt: `Inspiring visual representing success and achievement for: ${userGoal.substring(0, 150)}`,
          followUpQuestions: [
            'What are your main priorities for this project?',
            'What timeline and resources do you have available?'
          ]
        };
        
        console.log('[useProjectGeneration] Using intelligent fallback preserving user input:', parsedData);
      }

      console.log('[useProjectGeneration] ===== PROJECT GENERATION SUCCESSFUL =====');
      console.log('[useProjectGeneration] Generated project data:', parsedData);

      setIsGenerating(false);
      return parsedData;

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate project data';
      console.error('[useProjectGeneration] ===== PROJECT GENERATION FAILED =====');
      console.error('[useProjectGeneration] Error:', errorMessage);
      
      setError(errorMessage);
      setIsGenerating(false);
      throw err;
    }
  };

  return {
    generateProjectData,
    isGenerating,
    error
  };
} 